import { useContext, useState } from 'react';
import styled from 'styled-components';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex, FlexProps } from '@mantine/core';
import { ProfileBox } from '@dealstack/frontend/shared/components/ProfileBox/ProfileBox';
import { MiniListCard } from '@dealstack/frontend/shared/components/list/MiniListCard/MiniListCard';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useDealUpdateController } from '@dealstack/frontend/app/pages/Project/CreateDeal/useDealUpdateController.ts';
import { CreateDealForm } from '@dealstack/frontend/app/pages/Project/CreateDeal/CreateDealForm.tsx';
import { DeleteDealDrawer } from '@dealstack/frontend/app/pages/Project/DealSettings/DealGeneralSettings/DeleteDealDrawer';
import { NomineeStructureCard } from '@dealstack/frontend/app/pages/Project/DealSettings/DealGeneralSettings/NomineeStructureCard/NomineeStructureCard';
import { SettingsRowCard } from '@dealstack/frontend/shared/components/SettingsRowCard/SettingsRowCard.tsx';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { DealRestrictionToggle } from '@dealstack/frontend/shared/components/DealRestrictionToggle/DealRestrictionToggle';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { formatUserFullName } from '@dealstack/shared/lib/format/user.ts';
import { InfoBoxRow } from '@dealstack/frontend/shared/components/InfoBoxRow/InfoBoxRow';
import {
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useInviteDealUser } from '@dealstack/frontend/app/pages/Project/DealDetails/hooks';
import { DealSettingsView } from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.types';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { generatePath } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';

interface Props {
	onViewChange: (view: DealSettingsView) => void;
}

export const DealGeneralSettings = ({ onViewChange }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonUi = useTranslationCommonUi();

	const {
		deal,
		refetchDealData,
		dealsRefetch,
		getDealTeamRequest,
		team: users,
	} = useContext(DealContext);

	const { mutate: inviteDealUser, isPending } = useInviteDealUser();

	// CAP-1062: the deal flag is the master gate for every nominee surface, so hiding its only
	// entry point keeps the feature dark until the flag is rolled out.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;

	const [showInviteDrawer, setShowInviteDrawer] = useState(false);
	const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);

	const onUpdateSuccess = () => {
		void refetchDealData();
		void dealsRefetch();
	};

	const createDealController = useDealUpdateController({
		callback: onUpdateSuccess,
		isUpdate: true,
		deal,
	});

	const mappedUsers = users?.map((user) => ({
		id: user.id,
		value: {
			top: formatUserFullName(user),
			bottom: user.email,
		},
	}));

	const dealRelations = getResourceTypePermissions(ResourceType.DEAL);

	const permissions = dealRelations.map((relation) => ({
		label: tAdminDashboard(`invite_team_member.relation.${relation}`),
		value: relation,
	}));

	const handleDealInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!deal) {
			throw Error('Expect dealId to be defined');
		}
		inviteDealUser(
			{
				dealId: deal.id,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
			},
			{
				onSuccess: async () => {
					await getDealTeamRequest.refetch();
					onSuccess?.();
				},
			}
		);
	};

	useBreadcrumbs([
		{
			name: tCommonUi('breadcrumbs.deal_settings'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId: deal?.id ?? '' }),
		},
	]);

	return (
		<>
			<Flex
				sx={(theme) => ({
					flex: 1,
					border: `1px solid ${theme.colors.gray[2]}`,
					borderRadius: theme.radius.sm,
				})}
			>
				<StyledFormWrapper align="flex-start" p="1rem">
					<Flex direction="column" gap="1rem" style={{ flex: 1 }}>
						<ContainerWithHeader
							title={tAdminDashboard('deal.sidebar.about_label')}
							headerActions={
								<Button
									type="submit"
									data-testid={
										TEST_ELEMENT_IDS.ADMIN_DEAL_SETTINGS_PAGE_UPDATE_BUTTON
									}
									onClick={createDealController.createDealForm.handleSubmit?.(
										createDealController.onSubmit
									)}
									disabled={
										!createDealController.createDealForm.formState.isDirty ||
										createDealController.createDealRequest.isPending
									}
								>
									{tAdminDashboard('deal_settings.buttons.update_deal')}
								</Button>
							}
						>
							<CreateDealForm
								createDealController={createDealController}
								hideSubmitButton={true}
							/>
						</ContainerWithHeader>
						{nomineeStructuresEnabled && (
							<NomineeStructureCard
								dealId={deal?.id}
								hasNomineeStructure={deal?.hasNomineeStructure ?? false}
								onUpdated={onUpdateSuccess}
							/>
						)}
					</Flex>
				</StyledFormWrapper>
				<Flex
					direction="column"
					p="1rem"
					gap="1rem"
					w="33%"
					h="100%"
					style={{
						overflow: 'auto',
					}}
				>
					<ProfileBox
						text={
							<Flex direction="column" gap="1rem" sx={{ overflow: 'hidden' }}>
								<InfoBoxRow
									title={tAdminDashboard('deal_settings.labels.deal_name')}
									value={deal?.name}
								/>
								<InfoBoxRow
									title={tAdminDashboard('deal_settings.labels.target')}
									value={deal?.targetName}
								/>
							</Flex>
						}
					></ProfileBox>
					<MiniListCard
						title={tAdminDashboard('deal_settings.labels.deal_team')}
						items={mappedUsers}
						children={
							<Button
								variant="default"
								mt="1rem"
								ml="0.5rem"
								onClick={() => setShowInviteDrawer(true)}
							>
								{tAdminDashboard('deal_settings.buttons.invite_team_member')}
							</Button>
						}
						moreItemsClick={() => onViewChange(DealSettingsView.Members)}
					></MiniListCard>
					{deal?.id ? (
						<DealRestrictionToggle
							dealId={deal.id}
							isRestricted={deal.isRestricted ?? false}
							onUpdated={onUpdateSuccess}
						/>
					) : null}
					<SettingsRowCard
						tone="danger"
						title={tAdminDashboard('deal_settings.delete_deal.title', {
							defaultValue: 'Delete deal',
						})}
						description={tAdminDashboard('deal_settings.delete_deal.description', {
							defaultValue: 'Permanently delete this deal and all associated data.',
						})}
						action={
							<Button
								variant="subtle"
								color="red"
								data-testid={
									TEST_ELEMENT_IDS.ADMIN_DEAL_SETTINGS_PAGE_DELETE_BUTTON
								}
								onClick={() => {
									setShowDeleteDrawer(true);
								}}
							>
								{tAdminDashboard('v3.edit_deal.delete')}
							</Button>
						}
					/>
				</Flex>
			</Flex>
			{deal?.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					setIsOpen={setShowInviteDrawer}
					isLoading={isPending}
					onInvite={handleDealInvite}
					permissions={permissions}
					description={tAdminDashboard('invite_member_drawer.description')}
					roleLabel={tAdminDashboard('invite_member_drawer.form.role_field.label')}
				/>
			)}
			<DeleteDealDrawer
				opened={showDeleteDrawer}
				onClose={() => {
					setShowDeleteDrawer(false);
				}}
			/>
		</>
	);
};

const StyledFormWrapper = styled(Flex)<FlexProps>`
	flex: 1;
	border-right: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};

	form .fields-wrapper {
		flex-direction: row !important;
		flex-wrap: wrap;

		div.mantine-InputWrapper-root {
			width: calc(50% - 0.25rem);
		}
	}
`;
